/**
 * Algolia Predictive Search - Wayfair-Style
 * Simple autocomplete suggestions, trending searches, and recent searches
 * No products displayed - just clean text suggestions
 * @version 2.0.0
 */

class AlgoliaPredictiveSearch {
  static MOBILE_BREAKPOINT = 750;
  static RECENT_SEARCHES_KEY = 'algolia_recent_searches';
  static MAX_RECENT_SEARCHES = 5;
  static MAX_SUGGESTIONS = 10;
  static MAX_INIT_RETRIES = 50; // 50 * 100ms = 5 seconds max wait

  constructor() {
    this.config = window.algoliaConfig;
    this.searchClient = null;
    this.debounceTimer = null;
    this.isOpen = false;
    this.activeDropdown = null;
    this.activeInput = null;
    this.currentRequestId = 0;
    this.lastQuery = '';
    this.lastQueryID = null;
    this.lastHits = [];
    // The query and products count behind the dropdown on screen, for the
    // search event when a pick leaves without opening /search
    this.shownSearch = null;
    this.userToken = null;

    if (!this.config || !this.config.enabled) {
      this.log('Algolia not enabled, skipping predictive search');
      return;
    }

    if (!this.config.appId || !this.config.apiKey) {
      console.error('[Algolia Predictive] Missing credentials');
      return;
    }

    // Disable native search NOW, synchronously — not from inside init(),
    // which waits for the Algolia lite library to load (up to 5s, per the
    // comment there). That gap let native predictive-search's own keystroke
    // listener fire for any typing that happened before the library arrived.
    // Disabling only needs config (already available) and the native
    // PredictiveSearch class/element (both present as soon as the DOM and
    // search.js have parsed), so it doesn't need to wait on init() at all.
    this.disableNativeSearch();

    this.initRetries = 0;
    this.init();
  }

  log(...args) {
    if (this.config?.debug) {
      // eslint-disable-next-line no-console
      console.log('[Algolia Predictive]', ...args);
    }
  }

  isMobile() {
    return window.innerWidth < AlgoliaPredictiveSearch.MOBILE_BREAKPOINT;
  }

  init() {
    // Wait for algolia-search-lite.js to expose the global. Theme.liquid sets
    // an onload= handler on that script tag that dispatches Algolia:lite-ready
    // — listen for it instead of polling. Replaces a 100ms recursive
    // setTimeout that contributed to INP on every page (header/search input).
    if (typeof algoliasearch === 'undefined') {
      if (this._libsListenerAttached) return;
      this._libsListenerAttached = true;
      this.log('Waiting for Algolia:lite-ready event...');
      document.addEventListener('Algolia:lite-ready', () => {
        this._libsListenerAttached = false;
        this.init();
      }, { once: true });
      // Safety timeout — surfaces failure if onload never fires (SRI mismatch,
      // network error, content-blocker). 5s matches the previous polling budget.
      setTimeout(() => {
        if (typeof algoliasearch === 'undefined') {
          console.error('[Algolia Predictive] Library failed to load after 5s - predictive search disabled');
        }
      }, 5000);
      return;
    }

    // Lazy-appended on non-search pages, so this can run before
    // algolia-shared.js is parsed (see the truncateQuery guard below).
    // Look the wrapper up on each search, so a late AlgoliaShared still
    // applies instead of leaving the page on an unfiltered client.
    const rawClient = algoliasearch(this.config.appId, this.config.apiKey);
    const baseClient = {
      ...rawClient,
      search(queries, requestOptions) {
        const client = window.AlgoliaShared?.excludeUnlisted
          ? window.AlgoliaShared.excludeUnlisted(rawClient)
          : rawClient;
        return client.search(queries, requestOptions);
      },
    };
    this.searchClient = window.algoliaLogger
      ? window.algoliaLogger.wrapSearchClient(baseClient, 'PredictiveSearch')
      : baseClient;

    this.userToken = this.getOrCreateUserToken();
    // Also called earlier in the constructor, immediately on instantiation —
    // that call wins on pages where script order guarantees `search.js` (and
    // its native PredictiveSearch class) has already executed. On pages
    // where these two scripts are lazy-appended (theme.liquid's
    // appendScript(), no forced order between them), the earlier call can
    // race PredictiveSearch's own definition and silently no-op. Re-running
    // it here, after this async library-load wait has given `search.js`
    // plenty of time to have arrived, is a safety net for exactly that case
    // — idempotent either way.
    this.disableNativeSearch();
    this.enhanceSearchInputs();

    document.addEventListener('click', (e) => this.handleGlobalClick(e));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeDropdown();
    });
  }

  getOrCreateUserToken() {
    // Use shared utility for consistent userToken across all Algolia components
    if (window.AlgoliaShared?.getUserToken) {
      return window.AlgoliaShared.getUserToken();
    }
    // Fallback if shared utility not available
    const storageKey = 'algolia_user_token';
    let token = null;
    try {
      token = localStorage.getItem(storageKey);
      if (!token) {
        token = 'user_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
        localStorage.setItem(storageKey, token);
      }
    } catch (_e) {
      token = 'user_' + Math.random().toString(36).substring(2, 15);
    }
    return token;
  }

  // Recent searches management
  getRecentSearches() {
    try {
      const stored = localStorage.getItem(AlgoliaPredictiveSearch.RECENT_SEARCHES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (_e) {
      return [];
    }
  }

  addRecentSearch(query) {
    if (!query || query.length < 2) return;

    try {
      let recent = this.getRecentSearches();
      // Remove if already exists (will re-add at top)
      recent = recent.filter(s => s.toLowerCase() !== query.toLowerCase());
      // Add to beginning
      recent.unshift(query);
      // Limit size
      recent = recent.slice(0, AlgoliaPredictiveSearch.MAX_RECENT_SEARCHES);
      localStorage.setItem(AlgoliaPredictiveSearch.RECENT_SEARCHES_KEY, JSON.stringify(recent));
    } catch (e) {
      this.log('Failed to save recent search:', e.message);
    }
  }

  clearRecentSearches() {
    try {
      localStorage.removeItem(AlgoliaPredictiveSearch.RECENT_SEARCHES_KEY);
    } catch (_e) { /* ignore */ }
  }

  disableNativeSearch() {
    if (typeof PredictiveSearch !== 'undefined') {
      const noop = function() {};
      PredictiveSearch.prototype.renderSectionFromFetch = noop;
      PredictiveSearch.prototype.renderSectionFromCache = noop;
      this.log('Disabled native Shopify predictive search');
    }

    const predictiveSearchElement = document.querySelector('predictive-search');
    if (predictiveSearchElement) {
      predictiveSearchElement.toggleSearchState = function(isOpen) {
        this.classList.toggle('predictive-search-open', isOpen);
      };
      predictiveSearchElement.onFocus = function() {};
      this.log('Disabled native predictive-search element methods');
    }
  }

  enhanceSearchInputs() {
    const selectors = this.config.theme?.selectors?.searchInput || '.search__input, input[name="q"]';
    const inputs = document.querySelectorAll(selectors);

    inputs.forEach(input => {
      if (input.dataset.algoliaEnhanced) return;
      input.dataset.algoliaEnhanced = 'true';

      input.setAttribute('autocomplete', 'off');
      input.setAttribute('autocorrect', 'off');
      input.setAttribute('autocapitalize', 'off');
      input.setAttribute('spellcheck', 'false');

      const dropdown = this.createDropdown(input);

      input.addEventListener('input', (e) => this.handleInput(e, dropdown));
      input.addEventListener('focus', (e) => this.handleFocus(e, dropdown));
      input.addEventListener('click', (e) => {
        if (!this.isOpen) {
          this.handleFocus(e, dropdown);
        }
      });

      // Track searches on form submit
      const form = input.closest('form');
      if (form) {
        form.addEventListener('submit', () => {
          const query = input.value.trim();
          if (query) this.addRecentSearch(query);
        });
      }

      const resetBtn = input.closest('predictive-search')?.querySelector('button[type="reset"]');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          setTimeout(() => this.closeDropdown(), 10);
        });
      }

      this.addSearchButton(input);
      this.log('Enhanced search input');
    });
  }

  addSearchButton(input) {
    const searchField = input.closest('.search__field');
    if (!searchField) return;
    if (searchField.querySelector('.algolia-search-submit')) return;

    const form = input.closest('form');
    const formId = form?.id || '';

    const button = document.createElement('button');
    button.type = 'submit';
    button.className = 'algolia-search-submit';
    button.setAttribute('aria-label', 'Search');
    if (formId) button.setAttribute('form', formId);
    button.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M19 19L14.65 14.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    const resetBtn = searchField.querySelector('.search__reset');
    if (resetBtn) {
      resetBtn.insertAdjacentElement('afterend', button);
    } else {
      const searchIcon = searchField.querySelector('.search__field-icon');
      if (searchIcon) {
        searchIcon.insertAdjacentElement('beforebegin', button);
      } else {
        searchField.appendChild(button);
      }
    }
  }

  createDropdown(input) {
    const wrapper = input.closest('predictive-search') || input.closest('.search__form') || input.parentElement;
    const searchContent = wrapper.querySelector('.search__content');

    if (!searchContent) {
      const dropdown = document.createElement('div');
      dropdown.className = 'algolia-dropdown search__content page-width v-scrollable hidden';
      dropdown.setAttribute('role', 'listbox');
      const form = input.closest('form');
      if (form) {
        form.insertAdjacentElement('afterend', dropdown);
      } else {
        wrapper.appendChild(dropdown);
      }
      return dropdown;
    }

    searchContent.classList.add('algolia-search-content');

    const recommendations = searchContent.querySelector('.search__recommendations');
    if (recommendations) {
      searchContent._algoliaRecommendations = recommendations;
    }

    const resultsBox = searchContent.querySelector('.search__results');
    if (resultsBox) {
      searchContent._algoliaResultsBox = resultsBox;
    }

    return searchContent;
  }

  handleInput(event, dropdown) {
    // Guard: defer-load order is guaranteed under spec but Samsung Internet 29
    // and partial-loads (ad blocker dropping algolia-shared.js) can fire input
    // before AlgoliaShared is parsed.
    if (!window.AlgoliaShared?.truncateQuery) return;
    const query = window.AlgoliaShared.truncateQuery(event.target.value.trim());
    clearTimeout(this.debounceTimer);

    if (query.length === 0) {
      this.showInitialState(dropdown, event.target);
      return;
    }

    if (query.length < 2) return;

    this.setLoading(event.target, true);

    this.debounceTimer = setTimeout(() => {
      this.performSearch(query, dropdown, event.target);
    }, 200);
  }

  handleFocus(event, dropdown) {
    const query = event.target.value.trim();

    if (query.length === 0) {
      this.showInitialState(dropdown, event.target);
      return;
    }

    if (query.length >= 2 && dropdown.innerHTML.trim() && !dropdown.classList.contains('hidden')) {
      this.openDropdown(dropdown, event.target);
    }
  }

  async showInitialState(dropdown, input) {
    this.shownSearch = null;
    if (dropdown._algoliaRecommendations) {
      dropdown._algoliaRecommendations.style.display = 'none';
    }

    const resultsContainer = dropdown._algoliaResultsBox || dropdown.querySelector('.search__results');
    if (!resultsContainer) return;

    resultsContainer.style.display = 'block';

    const recentSearches = this.getRecentSearches();
    const trendingSearches = await this.getTrendingSearches();

    this.renderInitialState(resultsContainer, recentSearches, trendingSearches);
    this.openDropdown(dropdown, input);
  }

  async getTrendingSearches() {
    const cacheKey = 'algolia_trending_searches';
    const cacheExpiry = (this.config.cacheExpiry || 300) * 1000;

    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < cacheExpiry && data?.length > 0) {
          return data;
        }
      }
    } catch (_e) { /* ignore */ }

    if (this.config.suggestionsIndex) {
      try {
        const resp = await this.searchClient.search([{
          indexName: this.config.suggestionsIndex,
          query: '',
          params: { hitsPerPage: 50 }
        }]);

        const searches = (resp.results[0]?.hits || [])
          .map(hit => hit.query || hit.title || hit.text)
          .filter(Boolean);

        if (searches.length > 0) {
          try {
            localStorage.setItem(cacheKey, JSON.stringify({
              data: searches,
              timestamp: Date.now()
            }));
          } catch (_e) { /* ignore */ }
        }

        return searches;
      } catch (_e) {
        this.log('Failed to fetch trending searches:', _e.message);
      }
    }

    return this.config.popularSearches || [];
  }

  renderInitialState(container, recentSearches, trendingSearches) {
    const topTrending = trendingSearches.slice(0, 10);

    let html = '<div class="algolia-suggestions-dropdown">';

    // Recent searches section
    if (recentSearches.length > 0) {
      html += `
        <div class="algolia-section algolia-recent-section">
          <div class="algolia-section-header">
            <span class="algolia-section-title">Recent Searches</span>
            <button type="button" class="algolia-clear-recent" onclick="window.algoliaPredictiveSearch?.clearRecentAndRefresh()">Clear</button>
          </div>
          <ul class="algolia-suggestion-list">
            ${recentSearches.map(term => this.renderSuggestionItem(term, 'recent')).join('')}
          </ul>
        </div>
      `;
    }

    // Trending searches section
    if (topTrending.length > 0) {
      html += `
        <div class="algolia-section algolia-trending-section">
          <div class="algolia-section-header">
            <span class="algolia-section-title">Trending Now</span>
          </div>
          <ul class="algolia-suggestion-list">
            ${topTrending.map(term => this.renderSuggestionItem(term, 'trending')).join('')}
          </ul>
        </div>
      `;
    }

    html += '</div>';
    container.innerHTML = html;
  }

  renderSuggestionItem(term, type = 'suggestion') {
    const icon = type === 'recent'
      ? `<svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
           <circle cx="12" cy="12" r="10"></circle>
           <polyline points="12 6 12 12 16 14"></polyline>
         </svg>`
      : type === 'trending'
      ? `<svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
           <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
           <polyline points="17 6 23 6 23 12"></polyline>
         </svg>`
      : `<svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
           <circle cx="11" cy="11" r="8"></circle>
           <path d="M21 21l-4.35-4.35"></path>
         </svg>`;

    return `
      <li class="algolia-suggestion-item">
        <a href="/search?q=${encodeURIComponent(term)}" class="algolia-suggestion-link" data-query="${this.escapeHTML(term)}">
          ${icon}
          <span class="algolia-suggestion-text">${this.escapeHTML(term)}</span>
        </a>
      </li>
    `;
  }

  clearRecentAndRefresh() {
    this.clearRecentSearches();
    if (this.activeDropdown && this.activeInput) {
      this.showInitialState(this.activeDropdown, this.activeInput);
    }
  }

  handleGlobalClick(event) {
    // A pick that leaves without opening /search is the end of the search, so
    // it's recorded here. Suggestion and view-all links open /search, which
    // records it there. This runs ahead of the guard below because a pick can
    // land within 100ms of the latest results re-opening the dropdown.
    const pick = event.target.closest('.algolia-product-link, .algolia-collection-link, .algolia-page-link, .algolia-redirect-link');
    if (pick && this.shownSearch && this.activeDropdown?.contains(pick)) {
      window.AlgoliaShared?.reportSearch({
        query: this.shownSearch.query,
        resultsCount: this.shownSearch.resultsCount,
        surface: 'header',
        redirected: pick.classList.contains('algolia-redirect-link')
      });
    }

    if (this.openedAt && Date.now() - this.openedAt < 100) return;

    // Track clicks on product links from predictive search
    const productLink = event.target.closest('.algolia-product-link');
    if (productLink && this.lastQueryID) {
      const href = productLink.getAttribute('href');
      const productHandle = href?.match(/\/products\/([^/?]+)/)?.[1];

      if (productHandle && this.lastHits.length > 0) {
        const hitIndex = this.lastHits.findIndex(h => h.handle === productHandle);
        const hit = hitIndex >= 0 ? this.lastHits[hitIndex] : null;
        const objectID = hit?.objectID;

        if (objectID) {
          // Send click event
          if (typeof aa === 'function') {
            aa('clickedObjectIDsAfterSearch', {
              eventName: 'Product Clicked',
              index: this.config.indexName,
              objectIDs: [objectID],
              positions: [hitIndex + 1],
              queryID: this.lastQueryID
            });
          }

          // Store attribution for ATC tracking
          if (window.AlgoliaCartAttribution?.store) {
            window.AlgoliaCartAttribution.store({
              queryID: this.lastQueryID,
              objectID: objectID,
              handle: productHandle,
              indexName: this.config.indexName,
              position: hitIndex + 1,
              query: this.lastQuery
            });
          }

          this.log('Product click tracked from predictive search:', objectID);
        }
      }
    }

    // Track clicks on suggestion links
    const suggestionLink = event.target.closest('.algolia-suggestion-link');
    if (suggestionLink) {
      const query = suggestionLink.dataset.query;
      if (query) this.addRecentSearch(query);

      // On search page: intercept search suggestion clicks and submit form instead of navigating
      // This provides a smoother UX without full page reload
      const href = suggestionLink.getAttribute('href');
      if (document.body.classList.contains('template-search') && href?.startsWith('/search?q=')) {
        event.preventDefault();
        const urlParams = new URLSearchParams(href.split('?')[1]);
        const searchQuery = urlParams.get('q');
        if (searchQuery && this.activeInput) {
          this.activeInput.value = searchQuery;
          // Submit the form to trigger InstantSearch update
          const form = this.activeInput.closest('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }
        this.closeDropdown();
        return;
      }
    }

    // Handle clicks on view-all links similarly
    const viewAllLink = event.target.closest('.algolia-view-all-link');
    if (viewAllLink) {
      const href = viewAllLink.getAttribute('href');
      if (document.body.classList.contains('template-search') && href?.startsWith('/search?q=')) {
        event.preventDefault();
        const urlParams = new URLSearchParams(href.split('?')[1]);
        const searchQuery = urlParams.get('q');
        if (searchQuery && this.activeInput) {
          this.activeInput.value = searchQuery;
          const form = this.activeInput.closest('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }
        this.closeDropdown();
        return;
      }
    }

    if (this.activeDropdown &&
        !event.target.closest('.algolia-dropdown') &&
        !event.target.closest('.search__content') &&
        !event.target.closest('.search__input') &&
        !event.target.closest('input[name="q"]')) {
      this.closeDropdown();
    }
  }

  setLoading(input, isLoading) {
    const wrapper = input.closest('predictive-search');
    const resetBtn = wrapper?.querySelector('button[type="reset"]');
    if (isLoading) {
      wrapper?.setAttribute('loading', 'true');
      resetBtn?.classList.add('btn--loading');
    } else {
      wrapper?.removeAttribute('loading');
      resetBtn?.classList.remove('btn--loading');
    }
  }

  async performSearch(query, dropdown, input) {
    if (query === this.lastQuery) {
      this.setLoading(input, false);
      return;
    }
    this.lastQuery = query;

    const requestId = ++this.currentRequestId;

    try {
      const suggestions = await this.querySuggestions(query);

      if (requestId !== this.currentRequestId) return;

      this.lastQueryID = suggestions.queryID;
      this.lastHits = suggestions.hits;
      this.shownSearch = { query, resultsCount: suggestions.resultsCount };
      this.renderSuggestions(suggestions, query, dropdown, input);
      this.openDropdown(dropdown, input);
    } catch (error) {
      if (requestId !== this.currentRequestId) return;

      console.error('[Algolia Predictive] Search error:', error);
      if (dropdown._algoliaRecommendations) {
        dropdown._algoliaRecommendations.style.display = 'none';
      }
      const resultsContainer = dropdown._algoliaResultsBox || dropdown.querySelector('.search__results') || dropdown;
      resultsContainer.style.display = 'block';
      resultsContainer.innerHTML = this.renderError();
      this.openDropdown(dropdown, input);
    } finally {
      if (requestId === this.currentRequestId) {
        this.setLoading(input, false);
      }
    }
  }

  async querySuggestions(query) {
    const queries = [];
    // Queries are pushed conditionally per configured index, so the products
    // query's position in resp.results shifts depending on which of
    // suggestions/collections are enabled — track its real index rather than
    // assuming resp.results[0].
    let productsQueryIndex = -1;

    // Query suggestions index
    if (this.config.suggestionsIndex) {
      queries.push({
        indexName: this.config.suggestionsIndex,
        query,
        params: {
          hitsPerPage: 6,
          attributesToHighlight: ['query']
        }
      });
    }

    // Query collections index
    if (this.config.collectionsIndex) {
      queries.push({
        indexName: this.config.collectionsIndex,
        query,
        params: {
          hitsPerPage: 4,
          attributesToRetrieve: ['title', 'handle', 'meta.custom.frontend_title'],
          attributesToHighlight: ['title', 'meta.custom.frontend_title']
        }
      });
    }

    // Query products index (for SKU/UPC fallback when suggestions/collections empty)
    if (this.config.indexName) {
      productsQueryIndex = queries.length;
      queries.push({
        indexName: this.config.indexName,
        query,
        params: {
          hitsPerPage: 5,
          clickAnalytics: true,
          userToken: this.userToken,
          attributesToRetrieve: ['title', 'handle', 'vendor', 'sku', 'barcode', 'meta.custom.parent_sku', 'meta.custom.vendor_sku', 'meta.custom.parent_name', 'meta.custom.short_name'],
          attributesToHighlight: ['title', 'meta.custom.parent_name', 'meta.custom.short_name']
        }
      });
    }

    // Query pages index for content pages (FAQ, About, Contact, etc.)
    if (this.config.pagesIndex) {
      queries.push({
        indexName: this.config.pagesIndex,
        query,
        params: {
          hitsPerPage: 3,
          attributesToRetrieve: ['title', 'handle', 'body_html_safe'],
          attributesToHighlight: ['title']
        }
      });
    }

    if (queries.length === 0) return { suggestions: [], collections: [], products: [], pages: [], hits: [] };

    const resp = await this.searchClient.search(queries);

    // Algolia redirect rule for the query, shown as the first dropdown item.
    // It navigates only when picked: the shopper may still be typing ("fan"
    // on the way to "fan downrod"). Enter goes to /search, which follows it.
    // Redirect rules are configured in Algolia Dashboard → Rules, on the
    // PRODUCTS index specifically — not resp.results[0], which is whichever
    // query happens to be listed first (usually suggestions, not products).
    // Visual Editor stores in renderingContent.redirect, Manual Editor in userData
    const productsResult = productsQueryIndex >= 0 ? resp.results?.[productsQueryIndex] : null;
    const redirectUrl = productsResult?.renderingContent?.redirect?.url
      || productsResult?.userData?.[0]?.redirect
      || productsResult?.redirect?.url
      || null;

    const suggestions = [];
    const collections = [];
    const products = [];
    const pages = [];
    let resultIndex = 0;

    // Add query suggestions
    if (this.config.suggestionsIndex && resp.results[resultIndex]?.hits) {
      resp.results[resultIndex].hits.forEach(hit => {
        const text = hit.query || hit.text;
        if (text) suggestions.push(text);
      });
      resultIndex++;
    }

    // Add collections - pass both frontend_title and title for display/highlighting
    if (this.config.collectionsIndex && resp.results[resultIndex]?.hits) {
      resp.results[resultIndex].hits.forEach(hit => {
        if ((hit.meta?.custom?.frontend_title || hit.title) && hit.handle) {
          collections.push({
            title: hit.title,
            frontend_title: hit.meta?.custom?.frontend_title || null,
            handle: hit.handle,
            url: `/collections/${hit.handle}`
          });
        }
      });
      resultIndex++;
    }

    // Add products (for SKU/UPC fallback) - deduplicated by handle
    if (this.config.indexName && resp.results[resultIndex]?.hits) {
      const seenHandles = new Set();
      resp.results[resultIndex].hits.forEach(hit => {
        // Skip if we've already added this product (dedup by handle)
        if (seenHandles.has(hit.handle)) return;

        const productName = hit.meta?.custom?.parent_name || hit.meta?.custom?.short_name || hit.title;
        const brand = hit.vendor || '';
        // Format as "Brand - Product Name" if brand exists
        const displayTitle = brand ? `${brand} - ${productName}` : productName;
        if (displayTitle && hit.handle) {
          seenHandles.add(hit.handle);
          products.push({
            title: displayTitle,
            handle: hit.handle,
            url: `/products/${hit.handle}`,
            sku: hit.sku || hit.meta?.custom?.parent_sku || hit.meta?.custom?.vendor_sku || null,
            objectID: hit.objectID,
            position: products.length + 1
          });
        }
      });
      resultIndex++;
    }

    // Add pages (FAQ, About, Contact, etc.)
    if (this.config.pagesIndex && resp.results[resultIndex]?.hits) {
      resp.results[resultIndex].hits.forEach(hit => {
        if (hit.title && hit.handle) {
          pages.push({
            title: hit.title,
            handle: hit.handle,
            url: `/pages/${hit.handle}`
          });
        }
      });
    }

    return {
      suggestions,
      collections,
      products,
      pages,
      redirectUrl,
      queryID: productsResult?.queryID || null,
      hits: productsResult?.hits || [],
      resultsCount: productsResult ? productsResult.nbHits : 0
    };
  }

  renderSuggestions(results, query, dropdown, _input) {
    if (dropdown._algoliaRecommendations) {
      dropdown._algoliaRecommendations.style.display = 'none';
    }

    const resultsContainer = dropdown._algoliaResultsBox || dropdown.querySelector('.search__results') || dropdown;
    resultsContainer.style.display = 'block';

    const { suggestions = [], collections = [], products = [], pages = [], redirectUrl = null } = results;

    // Check if we have any results at all
    const hasSuggestions = suggestions.length > 0;
    const hasCollections = collections.length > 0;
    const hasProducts = products.length > 0;
    const hasPages = pages.length > 0;

    // If truly nothing found, show no results with top collections fallback
    if (!redirectUrl && !hasSuggestions && !hasCollections && !hasProducts && !hasPages) {
      resultsContainer.innerHTML = this.renderNoResults(query);
      return;
    }

    let html = '<div class="algolia-suggestions-dropdown">';

    if (redirectUrl) {
      html += `
        <div class="algolia-section">
          <ul class="algolia-suggestion-list">
            ${this.renderRedirectItem(redirectUrl, collections, query)}
          </ul>
        </div>
      `;
    }

    // Suggestions section
    if (hasSuggestions) {
      html += `
        <div class="algolia-section">
          <div class="algolia-section-header">
            <span class="algolia-section-title">Suggestions</span>
          </div>
          <ul class="algolia-suggestion-list">
            ${suggestions.map(term => this.renderSuggestionItemWithHighlight(term, query)).join('')}
          </ul>
        </div>
      `;
    }

    // Collections section
    if (hasCollections) {
      html += `
        <div class="algolia-section">
          <div class="algolia-section-header">
            <span class="algolia-section-title">Categories</span>
          </div>
          <ul class="algolia-suggestion-list">
            ${collections.map(col => this.renderCollectionItem(col, query)).join('')}
          </ul>
        </div>
      `;
    }

    // Products section - show when products have good relevance to query
    // Filter to products where query matches ~80% of characters in title or has significant overlap
    if (hasProducts) {
      const relevantProducts = products.filter(prod => this.isRelevantMatch(prod.title, query));
      if (relevantProducts.length > 0) {
        html += `
          <div class="algolia-section">
            <div class="algolia-section-header">
              <span class="algolia-section-title">Products</span>
            </div>
            <ul class="algolia-suggestion-list">
              ${relevantProducts.slice(0, 4).map(prod => this.renderProductItem(prod, query)).join('')}
            </ul>
          </div>
        `;
      }
    }

    // Pages section (FAQ, About, Contact, etc.)
    if (hasPages) {
      html += `
        <div class="algolia-section">
          <div class="algolia-section-header">
            <span class="algolia-section-title">Pages</span>
          </div>
          <ul class="algolia-suggestion-list">
            ${pages.map(page => this.renderPageItem(page, query)).join('')}
          </ul>
        </div>
      `;
    }

    html += `
      <div class="algolia-view-all">
        <a href="/search?q=${encodeURIComponent(query)}" class="algolia-view-all-link">
          View all results for "<strong>${this.escapeHTML(query)}</strong>"
        </a>
      </div>
    </div>`;

    resultsContainer.innerHTML = html;
  }

  /**
   * Render a redirect rule's destination as a dropdown item, named after the
   * matching collection when the same response returned it, otherwise after
   * the URL's last path segment ("ceiling-fans" -> "Ceiling Fans").
   */
  renderRedirectItem(url, collections, query) {
    // A rule's URL is typed by hand in the Algolia dashboard and may not parse
    // or decode ("50%-off"); the raw text still makes a usable label
    let path = url.split(/[?#]/)[0].replace(/\/$/, '');
    try {
      path = new URL(url, window.location.origin).pathname.replace(/\/$/, '');
    } catch { /* keep the raw path */ }
    const collection = collections.find(col => col.url === path);
    let segment = path.split('/').pop() || '';
    try {
      segment = decodeURIComponent(segment);
    } catch { /* keep the encoded segment */ }
    const destination = collection
      ? (collection.frontend_title || collection.title)
      : segment.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const label = this.config.searchRedirectLabel.replace('[destination]', destination);

    return `
      <li class="algolia-suggestion-item">
        <a href="${this.escapeHTML(url)}" class="algolia-suggestion-link algolia-redirect-link" data-query="${this.escapeHTML(query)}">
          <svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
          <span class="algolia-suggestion-text">${this.escapeHTML(label)}</span>
        </a>
      </li>
    `;
  }

  /**
   * Render a product item in the autocomplete dropdown
   * Used as fallback when no suggestions or collections match (e.g., SKU/UPC searches)
   */
  renderProductItem(product, query) {
    const lowerTitle = product.title.toLowerCase();
    const lowerQuery = query.toLowerCase();
    let displayText = this.escapeHTML(product.title);

    // Highlight matching portion in title
    const idx = lowerTitle.indexOf(lowerQuery);
    if (idx !== -1) {
      const before = product.title.substring(0, idx);
      const match = product.title.substring(idx, idx + query.length);
      const after = product.title.substring(idx + query.length);
      displayText = `${this.escapeHTML(before)}<strong>${this.escapeHTML(match)}</strong>${this.escapeHTML(after)}`;
    }

    return `
      <li class="algolia-suggestion-item">
        <a href="${product.url}" class="algolia-suggestion-link algolia-product-link" aria-label="Product: ${this.escapeHTML(product.title)}" data-query="${this.escapeHTML(product.title)}" data-objectid="${product.objectID || ''}" data-position="${product.position || ''}">
          <svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
            <line x1="7" y1="7" x2="7.01" y2="7"></line>
          </svg>
          <span class="algolia-suggestion-text">${displayText}</span>
        </a>
      </li>
    `;
  }

  renderCollectionItem(collection, query) {
    // Use frontend_title if available, otherwise fall back to title
    const displayTitle = collection.frontend_title || collection.title;
    const lowerTitle = displayTitle.toLowerCase();
    const lowerQuery = query.toLowerCase();
    let displayText = this.escapeHTML(displayTitle);

    // Highlight matching portion in display title
    const idx = lowerTitle.indexOf(lowerQuery);
    if (idx !== -1) {
      const before = displayTitle.substring(0, idx);
      const match = displayTitle.substring(idx, idx + query.length);
      const after = displayTitle.substring(idx + query.length);
      displayText = `${this.escapeHTML(before)}<strong>${this.escapeHTML(match)}</strong>${this.escapeHTML(after)}`;
    }

    return `
      <li class="algolia-suggestion-item">
        <a href="${collection.url}" class="algolia-suggestion-link algolia-collection-link" data-query="${this.escapeHTML(displayTitle)}">
          <svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
          </svg>
          <span class="algolia-suggestion-text">${displayText}</span>
        </a>
      </li>
    `;
  }

  renderPageItem(page, query) {
    const lowerTitle = page.title.toLowerCase();
    const lowerQuery = query.toLowerCase();
    let displayText = this.escapeHTML(page.title);

    // Highlight matching portion in title
    const idx = lowerTitle.indexOf(lowerQuery);
    if (idx !== -1) {
      const before = page.title.substring(0, idx);
      const match = page.title.substring(idx, idx + query.length);
      const after = page.title.substring(idx + query.length);
      displayText = `${this.escapeHTML(before)}<strong>${this.escapeHTML(match)}</strong>${this.escapeHTML(after)}`;
    }

    return `
      <li class="algolia-suggestion-item">
        <a href="${page.url}" class="algolia-suggestion-link algolia-page-link" data-query="${this.escapeHTML(page.title)}">
          <svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          <span class="algolia-suggestion-text">${displayText}</span>
        </a>
      </li>
    `;
  }

  renderSuggestionItemWithHighlight(term, query) {
    // Highlight matching portion
    const lowerTerm = term.toLowerCase();
    const lowerQuery = query.toLowerCase();
    let displayText = this.escapeHTML(term);

    const idx = lowerTerm.indexOf(lowerQuery);
    if (idx !== -1) {
      const before = term.substring(0, idx);
      const match = term.substring(idx, idx + query.length);
      const after = term.substring(idx + query.length);
      displayText = `${this.escapeHTML(before)}<strong>${this.escapeHTML(match)}</strong>${this.escapeHTML(after)}`;
    }

    return `
      <li class="algolia-suggestion-item">
        <a href="/search?q=${encodeURIComponent(term)}" class="algolia-suggestion-link" data-query="${this.escapeHTML(term)}">
          <svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="M21 21l-4.35-4.35"></path>
          </svg>
          <span class="algolia-suggestion-text">${displayText}</span>
        </a>
      </li>
    `;
  }

  renderNoResults(query) {
    // Get cached top collections for fallback suggestions
    const topCollections = this.getCachedTopCollections();
    const popularSearches = this.config.popularSearches || [];

    let html = `
      <div class="algolia-suggestions-dropdown">
        <div class="algolia-no-results">
          <p>No results found for "<strong>${this.escapeHTML(query)}</strong>"</p>
        </div>`;

    // Show top collections if available
    if (topCollections.length > 0) {
      html += `
        <div class="algolia-section">
          <div class="algolia-section-header">
            <span class="algolia-section-title">Browse Categories</span>
          </div>
          <ul class="algolia-suggestion-list">
            ${topCollections.slice(0, 5).map(col => `
              <li class="algolia-suggestion-item">
                <a href="${col.url}" class="algolia-suggestion-link algolia-collection-link">
                  <svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <span class="algolia-suggestion-text">${this.escapeHTML(col.title)}</span>
                </a>
              </li>
            `).join('')}
          </ul>
        </div>`;
    } else if (popularSearches.length > 0) {
      // Fallback to popular searches if no collections cached
      html += `
        <div class="algolia-section">
          <div class="algolia-section-header">
            <span class="algolia-section-title">Popular Searches</span>
          </div>
          <ul class="algolia-suggestion-list">
            ${popularSearches.slice(0, 5).map(term => `
              <li class="algolia-suggestion-item">
                <a href="/search?q=${encodeURIComponent(term)}" class="algolia-suggestion-link">
                  <svg class="algolia-suggestion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="M21 21l-4.35-4.35"></path>
                  </svg>
                  <span class="algolia-suggestion-text">${this.escapeHTML(term)}</span>
                </a>
              </li>
            `).join('')}
          </ul>
        </div>`;
    }

    html += `
        <div class="algolia-view-all">
          <a href="/search?q=${encodeURIComponent(query)}" class="algolia-view-all-link">
            Search for "<strong>${this.escapeHTML(query)}</strong>"
          </a>
        </div>
      </div>
    `;

    return html;
  }

  /**
   * Get cached top collections for no-results fallback
   * Uses localStorage cache to avoid extra API calls
   */
  getCachedTopCollections() {
    const cacheKey = 'algolia_top_collections';
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const cacheExpiry = (this.config.cacheExpiry || 300) * 1000;
        if (Date.now() - timestamp < cacheExpiry && data?.length > 0) {
          return data;
        }
      }
    } catch (_e) { /* ignore */ }

    // Trigger async fetch for next time (don't block rendering)
    this.fetchAndCacheTopCollections();
    return [];
  }

  /**
   * Fetch and cache top collections for fallback suggestions
   */
  async fetchAndCacheTopCollections() {
    if (!this.config.collectionsIndex || this._fetchingCollections) return;
    this._fetchingCollections = true;

    try {
      const resp = await this.searchClient.search([{
        indexName: this.config.collectionsIndex,
        query: '',
        params: {
          hitsPerPage: 10,
          attributesToRetrieve: ['title', 'handle', 'meta.custom.frontend_title', 'products_count']
        }
      }]);

      const collections = (resp.results[0]?.hits || [])
        .filter(hit => hit.handle && (hit.title || hit.meta?.custom?.frontend_title))
        .map(hit => ({
          title: hit.meta?.custom?.frontend_title || hit.title,
          handle: hit.handle,
          url: `/collections/${hit.handle}`
        }));

      if (collections.length > 0) {
        try {
          localStorage.setItem('algolia_top_collections', JSON.stringify({
            data: collections,
            timestamp: Date.now()
          }));
        } catch (_e) { /* ignore */ }
      }
    } catch (_e) {
      this.log('Failed to fetch top collections:', _e.message);
    } finally {
      this._fetchingCollections = false;
    }
  }

  renderError() {
    return `
      <div class="algolia-suggestions-dropdown">
        <div class="algolia-error">
          <p>Search temporarily unavailable. Please try again.</p>
        </div>
      </div>
    `;
  }

  openDropdown(dropdown, input) {
    dropdown.classList.remove('hidden');
    this.activeDropdown = dropdown;
    this.activeInput = input;
    this.isOpen = true;
    this.openedAt = Date.now();

    document.body.classList.add('search-open');
    input.setAttribute('aria-expanded', 'true');

    const wrapper = input.closest('predictive-search');
    wrapper?.classList.add('predictive-search-open');

    // Match dropdown width to input field container (desktop only)
    if (!this.isMobile()) {
      const searchField = input.closest('.search__field');
      if (searchField) {
        const fieldWidth = searchField.offsetWidth;
        dropdown.style.width = `${fieldWidth}px`;
      }
    }
  }

  closeDropdown() {
    if (this.activeDropdown) {
      this.activeDropdown.classList.add('hidden');
      this.activeDropdown.style.width = ''; // Reset inline width
      this.activeInput?.setAttribute('aria-expanded', 'false');
      this.activeInput?.closest('predictive-search')?.classList.remove('predictive-search-open');

      if (this.activeDropdown._algoliaRecommendations) {
        this.activeDropdown._algoliaRecommendations.style.display = '';
      }

      const resultsContainer = this.activeDropdown._algoliaResultsBox || this.activeDropdown.querySelector('.search__results');
      if (resultsContainer) {
        resultsContainer.style.display = '';
        const algoliaContent = resultsContainer.querySelector('.algolia-suggestions-dropdown');
        if (algoliaContent) algoliaContent.remove();
      }
    }
    document.body.classList.remove('search-open');
    this.activeDropdown = null;
    this.lastQuery = '';
    this.activeInput = null;
    this.isOpen = false;
  }

  escapeHTML(str) {
    if (!str) return '';
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  /**
   * Check if query is a relevant match for a title (~80% match threshold)
   * Uses multiple strategies: substring match, word overlap, and character similarity
   */
  isRelevantMatch(title, query) {
    if (!title || !query) return false;

    const lowerTitle = title.toLowerCase();
    const lowerQuery = query.toLowerCase().trim();

    // Direct substring match - always relevant
    if (lowerTitle.includes(lowerQuery)) return true;

    // Word-based matching: check if query words appear in title
    const queryWords = lowerQuery.split(/\s+/).filter(w => w.length >= 2);
    const titleWords = lowerTitle.split(/\s+/);

    if (queryWords.length > 0) {
      const matchedWords = queryWords.filter(qw =>
        titleWords.some(tw => tw.includes(qw) || qw.includes(tw))
      );
      // If 80%+ of query words match title words, it's relevant
      if (matchedWords.length / queryWords.length >= 0.8) return true;
    }

    // Character-level similarity for typo tolerance
    // Calculate what % of query characters appear in title (in order)
    let matchedChars = 0;
    let titleIdx = 0;
    for (const char of lowerQuery) {
      const foundIdx = lowerTitle.indexOf(char, titleIdx);
      if (foundIdx !== -1) {
        matchedChars++;
        titleIdx = foundIdx + 1;
      }
    }
    const charMatchRatio = matchedChars / lowerQuery.length;

    // 80% character match threshold
    return charMatchRatio >= 0.8;
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.algoliaPredictiveSearch = new AlgoliaPredictiveSearch();
  });
} else {
  window.algoliaPredictiveSearch = new AlgoliaPredictiveSearch();
}
